import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../api";
import { formatDate } from "../utils/dateFormat";
import { LoadingOverlay } from "../components/LoadingOverlay";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:30801";
const NAVY = "#1e3a8a";
const TEAL = "#00c9a7";

export default function InvitePage() {
  const { token } = useParams<{ token: string }>();
  const [info, setInfo]         = useState<any>(null);
  const [error, setError]       = useState("");
  const [loading, setLoading]   = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [email, setEmail]       = useState("");

  useEffect(() => {
    if (!token) return;
    api.get(`/api/auth/invite/validate/${token}`)
      .then(r => setInfo(r.data))
      .catch(e => setError(e.response?.data?.detail || "Invalid or expired invite link."))
      .finally(() => setLoading(false));
  }, [token]);

  async function handleAccept(e: React.FormEvent) {
    e.preventDefault();
    setAccepting(true); setError("");
    try {
      const r = await fetch(`${API_URL}/api/auth/invite/login/${token}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      if (!r.ok) { const d = await r.json(); setError(d.detail || "Failed to accept invite"); return; }
      window.location.href = "/security";
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setAccepting(false);
    }
  }

  if (loading) return (
    <div style={page}><p style={{ color: "#64748b" }}>Validating invite…</p></div>
  );

  return (
    <div style={page}>
      <LoadingOverlay active={accepting} />
      <div style={card}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 28 }}>
          <img src="/favicon.png" alt="HRxAI" style={{ width: 40, height: 40, borderRadius: 8, objectFit: "contain" }} />
          <span style={{ color: NAVY, fontWeight: 700, fontSize: 18 }}>
            HRx<span style={{ color: TEAL }}>AI</span>
          </span>
        </div>

        {error ? (
          <>
            <div style={{ fontSize: 40, marginBottom: 16 }}>⚠️</div>
            <h2 style={{ color: "#dc2626", margin: "0 0 8px" }}>Invite invalid</h2>
            <p style={{ color: "#64748b", fontSize: 14 }}>{error}</p>
            <p style={{ fontSize: 13, color: "#94a3b8", marginTop: 16 }}>
              Ask your admin to send you a new invitation link.
            </p>
          </>
        ) : (
          <>
            <div style={{ fontSize: 40, marginBottom: 16 }}>🎉</div>
            <h2 style={{ color: NAVY, margin: "0 0 8px" }}>You've been invited!</h2>
            <p style={{ color: "#334155", fontSize: 15, margin: "0 0 4px" }}>
              Join the <strong>{info.tenant_name}</strong> workspace
            </p>
            {info.email && (
              <p style={{ color: "#64748b", fontSize: 13, margin: "0 0 4px" }}>
                Invited as <strong>{info.email}</strong>
              </p>
            )}
            <p style={{ color: "#94a3b8", fontSize: 12, marginBottom: 20 }}>
              Expires: {formatDate(info.expires_at)}
            </p>

            {error && (
              <div style={{
                background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8,
                padding: "10px 14px", marginBottom: 14, fontSize: 13, color: "#dc2626", textAlign: "left",
              }}>
                {error}
              </div>
            )}

            <form onSubmit={handleAccept} style={{ textAlign: "left", marginBottom: 12 }}>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>
                Confirm your email to continue
              </label>
              <input
                type="email"
                autoFocus
                required
                placeholder={info.email || "your@email.com"}
                value={email}
                onChange={e => setEmail(e.target.value)}
                style={{
                  width: "100%", padding: "11px 14px", borderRadius: 10, fontSize: 14,
                  border: "1.5px solid #e2e8f0", outline: "none", boxSizing: "border-box",
                  color: "#1e293b", marginBottom: 14,
                }}
              />
              <button type="submit" disabled={!email || accepting} style={{
                width: "100%", padding: "13px 0", borderRadius: 10, border: "none",
                background: !email || accepting ? "#94a3b8" : `linear-gradient(135deg, ${NAVY}, ${TEAL})`,
                color: "white", fontSize: 15, fontWeight: 700,
                cursor: !email || accepting ? "not-allowed" : "pointer",
              }}>
                {accepting ? "Joining…" : "Accept & Set Up →"}
              </button>
            </form>
            <p style={{ color: "#94a3b8", fontSize: 12, marginTop: 8 }}>
              You'll set up your authenticator or passkey after joining.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

const page: React.CSSProperties = {
  minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
  background: "#f8fafc", fontFamily: "var(--font-sans)",
};
const card: React.CSSProperties = {
  background: "white", borderRadius: 14, padding: "40px 44px",
  boxShadow: "0 4px 24px rgba(0,0,0,0.1)", width: 380, textAlign: "center",
};
